// Reply privately (M63, D235): a group message's sender's direct chat,
// found or started, with the message quoted as plain text in its box
// through the drafts (D228). Nothing links the quote to the group message.
import { api, ApiError } from './api';
import { loadDrafts, saveDraft, type Drafts } from './drafts';
import {
  privateQuote,
  type ChatMessage,
} from '../components/chat/messageUtils';
import type { ChatConversation } from '../components/chat/ConversationDialogs';

export type PrivateReplyResult =
  | { kind: 'opened'; conversationId: string; drafts: Drafts; created: boolean }
  | { kind: 'blocked' }
  | { kind: 'failed'; error: unknown };

export async function startPrivateReply(input: {
  message: Pick<ChatMessage, 'type' | 'content' | 'sender' | 'e2ee'>;
  conversations: ChatConversation[];
  blockedIds: ReadonlySet<string>;
  familyId: string;
  userId: string;
  /** What I call the sender (relation name, D143), else their first name. */
  senderName: string;
}): Promise<PrivateReplyResult> {
  const sender = input.message.sender;
  if (!sender) return { kind: 'failed', error: null };
  // I blocked them: their chat is out of sight (D208).
  if (input.blockedIds.has(sender.id)) return { kind: 'blocked' };
  let conversationId =
    input.conversations.find(
      (c) =>
        c.type === 'direct' && c.participants.some((p) => p.id === sender.id)
    )?.id ?? null;
  let created = false;
  if (!conversationId) {
    try {
      const response = await api.createFamilyConversation(input.familyId, {
        type: 'direct',
        userId: sender.id,
      });
      conversationId = response.data.id;
      created = true;
    } catch (err) {
      if (err instanceof ApiError && err.code === 'BLOCKED') {
        return { kind: 'blocked' };
      }
      return { kind: 'failed', error: err };
    }
  }
  // Whatever the chat already had in its box follows the quote.
  const existing = loadDrafts(input.userId)[conversationId]?.text ?? '';
  const drafts = saveDraft(input.userId, conversationId, {
    text: privateQuote(input.message, input.senderName) + existing,
    familyId: input.familyId,
  });
  return { kind: 'opened', conversationId, drafts, created };
}
